import { useState, useEffect, useCallback, useMemo } from "react";
import "../Components/datagrid-override.css";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Button,
  IconButton,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import GradientIcon from '@mui/icons-material/Gradient';
import InventoryIcon from "@mui/icons-material/Inventory";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import DeleteConfirmationDialog from "./FormModal/DeleteConfirmationDialog";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import useVendorOptions from "../hooks/useVendorOptions";
import {
  oilValidationSchema,
  filterValidationSchema,
  receiveStockValidationSchema,
} from "./FormModal/validationSchemas";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const getOilPriority = (oil) => {
  const priority = Number(oil?.priority);
  return Number.isFinite(priority) && priority > 0
    ? priority
    : Number.MAX_SAFE_INTEGER;
};

const sortOils = (list = []) =>
  [...list].sort(
    (a, b) =>
      getOilPriority(a) - getOilPriority(b) ||
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
  );

const getFilterPriority = (filter) => {
  const priority = Number(filter?.priority);
  return Number.isFinite(priority) && priority > 0
    ? priority
    : Number.MAX_SAFE_INTEGER;
};

const sortFilters = (list = []) =>
  [...list].sort(
    (a, b) =>
      getFilterPriority(a) - getFilterPriority(b) ||
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
  );

function Oils() {
  const current_url = useLocation();
  const navigate = useNavigate();

  // Initial form values - DEFINE THESE FIRST
  const oilInit = {
    name: "",
    brand: "",
    type: "",
    priority: "",
    quantity: "",
    pricePerQuartz: "",
    pricePerVehicle: "",
    user_id: Helpers.authParentId,
  };

  const filterInit = {
    name: "",
    type: "",
    priority: "",
    quantity: "",
    buyPrice: "",
    sellPrice: "",
    invoiceNo: "",
    vendor: "",
    user_id: Helpers.authParentId,
  };

  const receiveStockInit = {
    quantity: "",
    vendor: "",
    invoiceNo: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
  };

  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [oils, setOils] = useState([]);
  const [filters, setFilters] = useState([]);
  const [currentItem, setCurrentItem] = useState(oilInit);
  const [perms, setPerms] = useState({});
  const [loadingOils, setLoadingOils] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);

  // Receive Stock Modal States
  const [openReceiveDialog, setOpenReceiveDialog] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveForm, setReceiveForm] = useState(receiveStockInit);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const { getVendorFieldOptions } = useVendorOptions(navigate);

  // Validation schemas

  // Form fields
  const oilFields = [
    { name: "name", label: "Oil Name", type: "text" },
    { name: "brand", label: "Brand", type: "text" },
    {
      name: "type",
      label: "Type",
      type: "select",
      options: [
        { label: "Conventional Oil", value: "Conventional Oil" },
        { label: "Synthetic Oil", value: "Synthetic Oil" },
      ],
    },
    {
      name: "priority",
      label: "Priority",
      type: "number",
      helperText: "Lower number appears higher in the list",
      inputProps: { min: 1, step: 1 },
    },
    { name: "quantity", label: "Quantity (Quartz)", type: "number" },
    { name: "pricePerQuartz", label: "Price Per Quartz ($)", type: "number" },
    { name: "pricePerVehicle", label: "Price Per Vehicle ($)", type: "number" },
  ];

  const filterFields = useMemo(
    () => [
      { name: "name", label: "Filter Name", type: "text" },
      { name: "type", label: "Type", type: "text" },
      {
        name: "priority",
        label: "Priority",
        type: "number",
        helperText: "Lower number appears higher in the list",
        inputProps: { min: 1, step: 1 },
      },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "buyPrice", label: "Buy Price ($)", type: "number" },
      { name: "sellPrice", label: "Sell Price ($)", type: "number" },
      { name: "invoiceNo", label: "Invoice No.", type: "text" },
      {
        name: "vendor",
        label: "Vendor",
        type: "select",
        options: getVendorFieldOptions(currentItem?.vendor),
      },
    ],
    [currentItem?.vendor, getVendorFieldOptions],
  );

  const receiveStockFields = useMemo(
    () => [
      { name: "quantity", label: "Quantity", type: "number" },
      {
        name: "vendor",
        label: "Vendor",
        type: "select",
        options: getVendorFieldOptions(receiveForm?.vendor),
      },
      { name: "invoiceNo", label: "Invoice No.", type: "text" },
      { name: "date", label: "Date", type: "date" },
      { name: "note", label: "Note", type: "text", multiline: true, rows: 2 },
    ],
    [getVendorFieldOptions, receiveForm?.vendor],
  );

  // Oil columns
  const oilColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 70,
      sortable: false,
      filterable: false,
    },
    { field: "name", headerName: "Oil Name", flex: 1, minWidth: 150 },
    { field: "brand", headerName: "Brand", flex: 1, minWidth: 120 },
    { field: "type", headerName: "Type", width: 150 },
    {
      field: "priority",
      headerName: "Priority",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    { field: "quantity", headerName: "Quantity", width: 100 },
    {
      field: "pricePerQuartz",
      headerName: "Price/Quartz",
      width: 120,
      renderCell: (params) => `$ ${params.value}`,
    },
    {
      field: "pricePerVehicle",
      headerName: "Price/Vehicle",
      width: 120,
      renderCell: (params) => `$ ${params.value}`,
    },
  ];

  // Filter columns
  const filterColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 70,
      sortable: false,
      filterable: false,
    },
    { field: "name", headerName: "Filter Name", flex: 1, minWidth: 150 },
    { field: "type", headerName: "Type", width: 120 },
    {
      field: "priority",
      headerName: "Priority",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    { field: "quantity", headerName: "Quantity", width: 100 },
    {
      field: "buyPrice",
      headerName: "Buy Price",
      width: 110,
      renderCell: (params) => `$ ${params.value}`,
    },
    {
      field: "sellPrice",
      headerName: "Sell Price",
      width: 110,
      renderCell: (params) => `$ ${params.value}`,
    },
    { field: "invoiceNo", headerName: "Invoice No.", width: 120 },
    { field: "vendor", headerName: "Vendor", flex: 1, minWidth: 120 },
  ];

  // Fetch data
  const getOils = useCallback(() => {
    setLoadingOils(true);
    axios
      .get(
        `${Helpers.baseUrl}/oils/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const sortedOils = sortOils(
          Array.isArray(response.data) ? response.data : [],
        );
        setOils(sortedOils);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoadingOils(false);
      });
  }, [navigate]);

  const getFilters = useCallback(() => {
    setLoadingFilters(true);
    axios
      .get(
        `${Helpers.baseUrl}/filters/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const sortedFilters = sortFilters(
          Array.isArray(response.data) ? response.data : [],
        );
        setFilters(sortedFilters);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoadingFilters(false);
      });
  }, [navigate]);

  // Search filtering
  const filteredOils = oils.filter((oil) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (oil.name || "").toLowerCase().includes(term) ||
      (oil.brand || "").toLowerCase().includes(term) ||
      (oil.type || "").toLowerCase().includes(term) ||
      String(oil.priority || "").toLowerCase().includes(term)
    );
  });

  const filteredFilters = filters.filter((filter) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (filter.name || "").toLowerCase().includes(term) ||
      (filter.type || "").toLowerCase().includes(term) ||
      String(filter.priority || "").toLowerCase().includes(term) ||
      (filter.vendor || "").toLowerCase().includes(term) ||
      (filter.invoiceNo || "").toLowerCase().includes(term)
    );
  });

  // CRUD operations
  const handleSave = (values) => {
    setBtnLoading(true);
    const moduleKey = activeTab === 0 ? "oils" : "filters";
    const addOrUpdate = isEditing ? "update" : "add";

    axios
      .post(
        `${Helpers.baseUrl}/${moduleKey}/${addOrUpdate}`,
        values,
        Helpers.headers,
      )
      .then((response) => {
        if (activeTab === 0) {
          getOils();
        } else {
          getFilters();
        }
        setCurrentItem(activeTab === 0 ? oilInit : filterInit);
        setIsEditing(false);
        setOpenFormDialog(false);
        Helpers.toast(
          "success",
          `${activeTab === 0 ? "Oil" : "Filter"} saved successfully`,
        );
        setBtnLoading(false);
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
        setBtnLoading(false);
      });
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setCurrentItem(item);
    setOpenFormDialog(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    // CRITICAL FIX: Create a fresh copy of the init object
    const initialValues = activeTab === 0 ? { ...oilInit } : { ...filterInit };
    setCurrentItem(initialValues);
    setOpenFormDialog(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    const moduleKey = activeTab === 0 ? "oils" : "filters";

    axios
      .get(
        `${Helpers.baseUrl}/${moduleKey}/delete/${itemToDelete._id}`,
        Helpers.headers,
      )
      .then((response) => {
        if (activeTab === 0) {
          getOils();
        } else {
          getFilters();
        }
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
        Helpers.toast(
          "success",
          `${activeTab === 0 ? "Oil" : "Filter"} deleted successfully`,
        );
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      });
  };

  // Receive Stock Functions
  const openReceiveModal = (item) => {
    setReceiveItem(item);
    setReceiveForm({
      ...receiveStockInit,
      vendor: item.vendor || "",
    });
    setOpenReceiveDialog(true);
  };

  const handleReceiveStock = (values) => {
    if (!receiveItem?._id) {
      Helpers.toast("error", "No item selected");
      return;
    }

    setReceiveLoading(true);
    const moduleKey = activeTab === 0 ? "oils" : "filters";

    const payload = {
      _id: receiveItem._id,
      quantity: parseInt(values.quantity, 10) || 0,
      vendor: values.vendor || "",
      invoiceNo: values.invoiceNo || "",
      date: values.date || new Date().toISOString().slice(0, 10),
      note: values.note || "",
    };

    axios
      .post(
        `${Helpers.baseUrl}/${moduleKey}/receive-stock`,
        payload,
        Helpers.headers,
      )
      .then((response) => {
        // Update receiveItem with fresh data from backend
        if (
          response.data &&
          response.data[moduleKey === "oils" ? "oil" : "filter"]
        ) {
          setReceiveItem(
            response.data[moduleKey === "oils" ? "oil" : "filter"],
          );
        } else if (response.data) {
          setReceiveItem(response.data);
        }

        // Reset form but keep vendor
        setReceiveForm({
          quantity: "",
          vendor: values.vendor || "",
          invoiceNo: "",
          date: new Date().toISOString().slice(0, 10),
          note: "",
        });

        // Refresh the list
        if (activeTab === 0) {
          getOils();
        } else {
          getFilters();
        }

        Helpers.toast("success", "Stock received successfully");
        // Keep dialog open to show updated history
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
      })
      .finally(() => {
        setReceiveLoading(false);
      });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const getPerms = useCallback(() => {
    const link = current_url.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find(
        (permission) => permission.tab_link === link,
      );
      setPerms(perm || {});
    }
  }, [current_url.pathname]);

  useEffect(() => {
    getOils();
    getFilters();
    getPerms();
  }, [getFilters, getOils, getPerms]);

  const canCreate =
    Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate =
    Helpers.authUser.user_role == null || perms.can_update === 1;
  const canDelete =
    Helpers.authUser.user_role == null || perms.can_delete === 1;

  // Actions renderer
  const actionsRenderer = (params) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 0.5,
      }}
    >
      {canUpdate && (
        <IconButton
          onClick={() => openReceiveModal(params.row)}
          color="info"
          size="small"
          title="Receive Stock"
        >
          <InventoryIcon fontSize="small" />
        </IconButton>
      )}
      {canUpdate && (
        <IconButton
          onClick={() => handleEdit(params.row)}
          color="success"
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          onClick={() => handleDelete(params.row)}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Enhanced columns with actions
  const enhancedOilColumns = [
    ...oilColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: actionsRenderer,
    },
  ];

  const enhancedFilterColumns = [
    ...filterColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: actionsRenderer,
    },
  ];

  // Prepare rows
  const oilRows = filteredOils.map((oil, index) => ({
    ...oil,
    id: oil._id,
    serial: index + 1,
  }));

  const filterRows = filteredFilters.map((filter, index) => ({
    ...filter,
    id: filter._id,
    serial: index + 1,
  }));

  const breadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
    },
    { label: "Oils & Filters", path: null },
  ];

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {item.icon}
                  {item.label}
                </Typography>
              ) : (
                <MuiLink
                  key={index}
                  component={Link}
                  to={item.path}
                  underline="hover"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "0.875rem",
                    "&:hover": { color: "white" },
                  }}
                >
                  {item.icon}
                  {item.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Oils & Filters Management
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
              >
                Manage your automotive oils and filters inventory
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                icon={
                  <OilBarrelIcon sx={{ color: "white !important" }} />
                }
                label={`${filteredOils.length} Oils`}
                size="small"
                sx={{
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  fontWeight: 600,
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              />
              <Chip
                icon={<GradientIcon sx={{ color: "white !important" }} />}
                label={`${filteredFilters.length} Filters`}
                size="small"
                sx={{
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  fontWeight: 600,
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              />
            </Stack>
          </Box>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            mb: 5.25,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                setSearch("");
              }}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  minHeight: "56px",
                },
              }}
            >
              <Tab
                icon={<OilBarrelIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Oils"
              />
              <Tab
                icon={<GradientIcon sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label="Filters"
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
              flexShrink: 0,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                All {activeTab === 0 ? "Oils" : "Filters"}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ minWidth: { sm: "auto", md: "50%" } }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}
                />
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddNew}
                    size="small"
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      backgroundColor: "#28a745",
                      "&:hover": { backgroundColor: "#218838" },
                    }}
                  >
                    Add {activeTab === 0 ? "Oil" : "Filter"}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {activeTab === 0 && (
              <ReusableDataGrid
                rows={oilRows}
                loading={loadingOils}
                columns={enhancedOilColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                noRowsMessage="No oils available to display"
                autoHeight={false}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                  },
                }}
              />
            )}
            {activeTab === 1 && (
              <ReusableDataGrid
                rows={filterRows}
                loading={loadingFilters}
                columns={enhancedFilterColumns}
                pageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                noRowsMessage="No filters available to display"
                autoHeight={false}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.04)",
                  },
                }}
              />
            )}
          </Box>
        </Paper>
      </Container>
      <ReusableFormDialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setCurrentItem(activeTab === 0 ? oilInit : filterInit);
          setIsEditing(false);
        }}
        title={
          isEditing
            ? `Edit ${activeTab === 0 ? "Oil" : "Filter"}: ${currentItem.name || ""}`
            : `Add New ${activeTab === 0 ? "Oil" : "Filter"}`
        }
        initialValues={currentItem}
        validationSchema={
          activeTab === 0 ? oilValidationSchema : filterValidationSchema
        }
        fields={activeTab === 0 ? oilFields : filterFields}
        onSubmit={handleSave}
        loading={btnLoading}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title={`Delete ${activeTab === 0 ? "Oil" : "Filter"}`}
        itemName={itemToDelete?.name}
        itemType={activeTab === 0 ? "Oil" : "Filter"}
        warningText="This action cannot be undone. All associated records will remain but will no longer be linked to this item."
        loading={deleteLoading}
      />
      <ReusableFormDialog
        open={openReceiveDialog}
        onClose={() => {
          setOpenReceiveDialog(false);
          setReceiveForm(receiveStockInit);
          setReceiveItem(null);
        }}
        title={`Receive Stock: ${receiveItem ? (activeTab === 0 ? `${receiveItem.name} (${receiveItem.brand})` : receiveItem.name) : ""}`}
        initialValues={receiveForm}
        validationSchema={receiveStockValidationSchema}
        fields={receiveStockFields}
        onSubmit={handleReceiveStock}
        loading={receiveLoading}
        maxWidth="md"
      >
        {receiveItem &&
          Array.isArray(receiveItem.purchaseHistory) &&
          receiveItem.purchaseHistory.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  Purchase History
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 250,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Date
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Qty
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Vendor
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Invoice
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                        >
                          Note
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...receiveItem.purchaseHistory]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 10)
                        .map((entry, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <TableCell>{formatDate(entry.date)}</TableCell>
                            <TableCell>{entry.quantity}</TableCell>
                            <TableCell>{entry.vendor || "-"}</TableCell>
                            <TableCell>{entry.invoiceNo || "-"}</TableCell>
                            <TableCell>{entry.note || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
      </ReusableFormDialog>
    </Box>
  );
}

export default Oils;
